/**
 * API Service for communicating with BARCODE-DEMO backend
 * This is the foundation for Action 1 - establishing backend communication
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export class ApiService {
  /**
   * Test the connection to the backend API
   */
  static async testConnection(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Connection test failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check the health of the backend API
   */
  static async checkHealth(): Promise<{ healthy: boolean; data?: any }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/health`);
      const data = await response.json();
      return { healthy: data.status === 'healthy', data };
    } catch (error) {
      console.error('Health check failed:', error);
      return { healthy: false };
    }
  }

  /**
   * Get camera preview frame
   */
  static async getCameraPreview(): Promise<{
    frame?: string;
    width?: number;
    height?: number;
    error?: string
  }> {
    try {
      // Add cache-busting timestamp to prevent stale frames
      const timestamp = Date.now();
      const response = await fetch(`${API_BASE_URL}/api/camera/preview?t=${timestamp}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        return { error: data.error };
      }

      return {
        frame: data.frame,
        width: data.width,
        height: data.height
      };
    } catch (error) {
      console.error('Failed to get camera preview:', error);
      return {
        error: error instanceof Error ? error.message : 'Failed to get camera preview'
      };
    }
  }

  /**
   * Capture photo from camera and save to disk
   */
  static async capturePhoto(): Promise<{
    success: boolean;
    filename?: string;
    filepath?: string;
    date?: string;
    timestamp?: string;
    width?: number;
    height?: number;
    error?: string;
  }> {
    try {
      // Add cache-busting timestamp and no-cache headers
      const timestamp = Date.now();
      const response = await fetch(`${API_BASE_URL}/api/camera/capture?t=${timestamp}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to capture photo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to capture photo'
      };
    }
  }

  /**
   * Trigger product detection
   */
  static async detectProduct(model?: string): Promise<{
    success: boolean;
    product_name?: string;
    barcode?: string;
    detection_method?: string;
    confidence?: number;
    brand?: string;
    weight_valid?: boolean;
    detected_weight?: number;
    expected_weight?: number;
    weight_warning?: string;
    error?: string;
    detection_time?: number;
    details?: any;
  }> {
    try {
      const url = model
        ? `${API_BASE_URL}/api/detect?model=${encodeURIComponent(model)}`
        : `${API_BASE_URL}/api/detect`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success && data.error) {
        return {
          success: false,
          error: data.error
        };
      }

      return data;
    } catch (error) {
      console.error('Product detection failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Detection failed'
      };
    }
  }

  /**
   * Get scale status
   */
  static async getScaleStatus(): Promise<{
    success: boolean;
    connected?: boolean;
    monitoring?: boolean;
    current_weight?: number;
    baseline_weight?: number;
    accumulated_weight?: number;
    is_stable?: boolean;
    last_update?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/status`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, ...data };
    } catch (error) {
      console.error('Failed to get scale status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get scale status'
      };
    }
  }

  /**
   * Get current weight data with smart fluctuation detection
   */
  static async getWeight(): Promise<{
    success: boolean;
    current_weight?: number;
    baseline_weight?: number;
    experience_baseline?: number | null;
    accumulated_weight?: number;
    is_stable?: boolean;
    is_fluctuating?: boolean;
    severity?: 'none' | 'slight' | 'significant';
    in_experience?: boolean;
    deviation?: number;
    expected_weight?: number;
    should_block?: boolean;
    returned_to_baseline?: boolean;
    unit?: string;
    last_update?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/weight`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get weight:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get weight'
      };
    }
  }

  /**
   * Set baseline weight (empty tray)
   */
  static async setBaseline(): Promise<{
    success: boolean;
    baseline_weight?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/baseline`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to set baseline:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set baseline'
      };
    }
  }

  /**
   * Check for weight fluctuations with smart context-aware detection
   */
  static async checkFluctuation(): Promise<{
    success: boolean;
    is_fluctuating?: boolean;
    severity?: 'none' | 'slight' | 'significant';
    in_experience?: boolean;
    deviation?: number;
    expected_weight?: number;
    current_weight?: number;
    should_block?: boolean;
    returned_to_baseline?: boolean;
    baseline_weight?: number;
    experience_baseline?: number | null;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/fluctuation`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to check fluctuation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check fluctuation'
      };
    }
  }

  /**
   * Set experience baseline weight (expected weight during user flow)
   */
  static async setExperienceBaseline(weight: number | null): Promise<{
    success: boolean;
    experience_baseline?: number | null;
    baseline_weight?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/experience-baseline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ weight })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to set experience baseline:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set experience baseline'
      };
    }
  }

  /**
   * Set items weight (weight with items on tray)
   */
  static async setItemsWeight(weight: number | null): Promise<{
    success: boolean;
    items_weight?: number | null;
    baseline_weight?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scale/items-weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ weight })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to set items weight:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set items weight'
      };
    }
  }
}

// Export a singleton instance for convenience
export default ApiService;
